// [목적] / 홈 페이지 — Blue Gradient Hero + 통계 Row + 축제 섹션 + 컬렉션 + 추천 피드
// [이유] design-align-v2: 프로토타입 시안(gangwonfolio-web.html) 대비 Hero 그라디언트 카드·인라인 입력·
//         빠른 칩, 이어서 작성/통계 Row, 강원 축제 섹션 추가. 기존 컬렉션·추천 피드 섹션 유지.
//         fe-home-festivals-live: 축제 섹션 데이터 소스를 mock(FESTIVALS 상수) → GET /festivals 실 API 로 교체.
//         레이아웃(히어로 배너 + 카드 3개 + 전체 보기)은 변경 없음 — 데이터 소스만 교체.
//         M7 P1(홈 실데이터): 통계 Row(0/221/34 mock) → useUserStats(GET /users/me/stats), 비로그인 시 로그인
//         유도 배너로 대체. 큐레이티드 컬렉션 mock(COLLECTIONS) → collectionClient.list({kind:'official'})
//         실데이터(ExplorePage/HomeMobile 과 동일 패턴). "이어서 작성" 카드 → useFolioFeed(author:'me',
//         sort:'latest', limit:1) 최근 폴리오 1건(없으면 "새 폴리오 만들기" CTA).
// [사이드 이팩트] 기존 HomeMobile 분기 유지. 상태: chatInput(string) useState 추가.
//                 navigate('/chat') 시 URL search param ?q= 으로 입력값 전달.
//                 useFestivals(useQuery) 추가 — GET /festivals 호출.
//                 useUserStats/useFolioFeed(author:'me')/collectionClient.list 신규 호출 3건 추가(모두 캐시 분리).
// [수정 시 고려사항] "이어서 작성"은 BE 에 updated_at 정렬이 없어(FoliosRepository sort: latest/likes/forks 뿐)
//                   created_at DESC(sort:'latest')를 최근 활동의 근사치로 사용한다(ponytail: 정확한 "최근 수정"이
//                   필요해지면 BE sort='updated' 추가 후 교체).
//                   현재 실데이터는 전부 status='ended' — 섹션이 ended 만 있어도 자연스럽게 보이는지 유지 필요.

import { useState } from 'react';
import { Box, ButtonBase, InputBase, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Collection, Folio } from '@gangwoncanvas/types';

import { TopBar } from '../components/layout/TopBar.js';
import { FolioCardSkeleton } from '../components/feedback/Skeleton.js';
import { FolioLikeChip } from '../components/folio/FolioLikeChip.js';
import { useShell } from '../hooks/useShell.js';
import { useAuth } from '../hooks/useAuth.js';
import { useFolioFeed } from '../hooks/useFolioFeed.js';
import { useFestivals } from '../hooks/useFestivals.js';
import { useUserStats } from '../hooks/useUserProfile.js';
import { FESTIVAL_STATUS_META } from '../utils/festivalStatusMeta.js';
import { folioAuthorLabel } from '../utils/folioAuthorLabel.js';
import { LineIcon, type LineIconName } from '../components/icons/LineIcons.js';
import * as collectionClient from '../api/collection.client.js';
import { queryKeys } from '../api/queryKeys.js';
import {
  formatFestivalLocation,
  formatFestivalPeriod,
  truncateOverview,
  type Festival,
} from '../api/festivals.client.js';

import { HomeMobile } from './mobile/HomeMobile.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const BORDER_SOFT = c.line.soft[L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const WARM = c.warm[500][L];
const PAPER0 = c.paper[0][L];
// [2026-09-18] Blue Gradient Hero — 흰 텍스트 배경 전용 진한 그린. accent[500](#2BB673) 는 흰 텍스트
// 기준 2.61:1 로 AA 미달이었다("GOOD MORNING"·헤딩·서브카피·"그리기 →", docs/qa/2026-09-17_contrast-measure.md).
// accent[600](#0F7A46) 은 5.40:1.
const ACCENT_STRONG = c.accent[600][L];

// ─── 축제 상태 배지 메타 ─────────────────────────────────────────────────────────

/**
 * [목적] 축제 status → 배지 라벨/색 매핑 — 진행 중(초록)/예정(브랜드)/종료(회색)
 * [이유] fe-home-festivals-live: 실 API 의 status(ongoing/upcoming/unknown/ended) 를 배지로 시각화.
 *        색만으로 정보 전달하지 않도록 label 텍스트를 항상 함께 표시(WCAG 2.1 AA).
 * [사이드 이팩트] HomeFestivalHero/HomeFestivalCard 가 이 맵을 참조.
 * [수정 시 고려사항] status enum 확장 시(FestivalStatusEnum) 이 맵도 동시 갱신 필요.
 */
// [수정: 2026-09-20] 공유 모듈로 이전 — 소형 배지 텍스트는 textColor(AA 충족) 사용.
const STATUS_META = FESTIVAL_STATUS_META;

const QUICK_CHIPS = ['🐾 반려견과', '📅 2박 3일', '🚄 KTX 강릉', '🏄 서핑', '☕카페 위주'];

// ─── HomePage ─────────────────────────────────────────────────────────────────

/**
 * [목적] / 홈 페이지 진입점 — 모바일/데스크톱 분기
 * [이유] design-align-v2 Hero 그라디언트 카드 + 통계 Row + 축제 섹션
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] HomeMobile 은 별도 Wave 에서 디자인 동기화 예정
 */
export function HomePage(): JSX.Element {
  const { viewport } = useShell();
  return viewport.isMobile ? <HomeMobile /> : <HomeDesktop />;
}

// ─── HomeDesktop ──────────────────────────────────────────────────────────────

/**
 * [목적] 홈 데스크톱 레이아웃 — Hero 그라디언트 + 통계 Row + 축제 + 컬렉션 + 추천 피드
 * [이유] design-align-v2 시안 포팅. fe-home-festivals-live: 축제 섹션은 useFestivals(GET /festivals) 실데이터.
 * [사이드 이팩트] chatInput state 추가. useFestivals 훅 호출 추가.
 * [수정 시 고려사항] 통계 Row 는 여전히 mock 수치 — 실데이터 연결 시 교체 필요.
 */
function HomeDesktop(): JSX.Element {
  const navigate = useNavigate();
  const { viewport, openSidebar } = useShell();
  const { status, user } = useAuth();
  const [chatInput, setChatInput] = useState('');
  const cols = viewport.isDesktop ? 3 : viewport.isTablet ? 2 : 1;
  const colCols = viewport.isDesktop ? 4 : viewport.isTablet ? 3 : 2;

  const {
    allFolios,
    isLoading: feedLoading,
    error: feedError,
  } = useFolioFeed({ sort: 'trending', limit: 12 });

  // M7 P1: 홈 통계 카드 실데이터 — 비로그인 시 enabled=false(useUserStats 내부에서 가드)
  const { stats, isLoading: statsLoading } = useUserStats();

  // M7 P1: "이어서 작성" 카드 — 본인 최근 폴리오 1건(sort='latest' 이 BE 유일 근사치, ponytail 상단 주석 참조)
  const { allFolios: myRecentFolios, isLoading: myRecentLoading } = useFolioFeed({
    author: 'me',
    sort: 'latest',
    limit: 1,
    enabled: status === 'authenticated',
  });
  const continuingFolio = myRecentFolios[0];

  // M7 P1: 큐레이티드 컬렉션 mock → 공식 컬렉션 실데이터(ExplorePage/HomeMobile 과 동일 client/쿼리키 재사용)
  const { data: collectionData, isLoading: collectionsLoading } = useQuery({
    queryKey: queryKeys.collection.list({ kind: 'official', limit: 12 }),
    queryFn: () => collectionClient.list({ kind: 'official', limit: 12 }),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });
  const officialCollections = collectionData?.items ?? [];

  // fe-home-festivals-live: GET /festivals(서버 정렬 ongoing→upcoming→unknown→ended) — 첫 항목=히어로, 다음 3개=카드
  const { data: festivals, isLoading: festivalsLoading, error: festivalsError } = useFestivals(12);
  const featuredFest = festivals?.[0];
  const smallFests = festivals?.slice(1, 4) ?? [];
  // 에러/빈 목록이면 섹션 자체를 숨겨 홈이 죽지 않게 한다(로딩 중엔 스켈레톤 표시)
  const showFestivalSection = festivalsLoading || (!festivalsError && !!featuredFest);

  const dateLabel = new Date()
    .toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    })
    .toUpperCase();

  // [수정: QA-P3-03] "GOOD MORNING" 이 시간과 무관하게 하드코딩되어 있어 오후에 방문해도
  // 계속 "GOOD MORNING" 으로 표시되는 문제가 있었다. 사용자 로컬 시각과 무관하게 KST 기준으로 통일한다.
  const kstHour = Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Seoul',
      hour: 'numeric',
      hourCycle: 'h23',
    }).format(new Date()),
  );
  const greeting =
    kstHour >= 5 && kstHour < 12
      ? 'GOOD MORNING'
      : kstHour >= 12 && kstHour < 18
        ? 'GOOD AFTERNOON'
        : kstHour >= 18 && kstHour < 22
          ? 'GOOD EVENING'
          : 'GOOD NIGHT';

  function handleStartChat() {
    const q = chatInput.trim();
    navigate(q ? `/chat?q=${encodeURIComponent(q)}` : '/chat');
  }

  function handleChipClick(chip: string) {
    setChatInput((prev) => (prev ? `${prev} ${chip}` : chip));
  }

  return (
    <>
      {/* gwPulse keyframe for festival live badge */}
      <style>{`@keyframes gwPulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>

      <TopBar title="홈" mobile={viewport.isMobile} onBurger={openSidebar} />
      <Box
        sx={{
          p: {
            xs: `${tokens.spacing[6]}px ${tokens.spacing[4]}px 60px`,
            md: `${tokens.spacing[8]}px ${tokens.spacing[8]}px 60px`,
          },
          maxWidth: 1320,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* ── A. Blue Gradient Hero ────────────────────────────────────────── */}
        {/* [2026-09-18 대비 상향] 구 중간 stop(#3B7DEE)·끝 stop(#2BB673) 위에서 흰 텍스트가
            3.92 / 2.61:1 로 AA 미달이었다(docs/qa/2026-09-17_contrast-measure.md). 중간 stop 은
            제거(두 stop 만으로도 CSS 가 동일하게 보간)하고 끝 stop 은 accent[600](5.40:1)으로 교체 —
            브랜드 블루 → 그린 톤은 유지. */}
        <Box
          component="section"
          aria-label="강원 여행 시작"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '22px',
            background: `linear-gradient(135deg,${BRAND} 0%,${ACCENT_STRONG} 150%)`,
            p: { xs: '28px 24px', md: '40px 44px' },
            mb: '38px',
          }}
        >
          {/* Decorative circles */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 220,
              height: 220,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,.08)',
              pointerEvents: 'none',
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              bottom: -80,
              left: -40,
              width: 260,
              height: 260,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,.06)',
              pointerEvents: 'none',
            }}
          />

          {/* Top label */}
          <Typography
            aria-hidden="true"
            sx={{
              fontSize: 11,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              color: 'rgba(255,255,255,.8)',
              letterSpacing: '0.5px',
              mb: '10px',
            }}
          >
            {greeting} · {dateLabel}
          </Typography>

          {/* Heading */}
          {status === 'authenticated' && user ? (
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 23, md: 31 },
                fontWeight: 800,
                color: '#fff',
                letterSpacing: '-0.8px',
                lineHeight: 1.2,
                m: 0,
                mb: '10px',
              }}
            >
              {user.displayName}님, 오늘은
              <br />
              어떤 강원을 그려볼까요?
            </Typography>
          ) : (
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 23, md: 31 },
                fontWeight: 800,
                color: '#fff',
                letterSpacing: '-0.8px',
                lineHeight: 1.2,
                m: 0,
                mb: '10px',
              }}
            >
              강원도 여행,
              <br />한 줄로 시작하세요
            </Typography>
          )}

          {/* Subtext */}
          <Typography
            sx={{
              fontSize: 13.5,
              color: 'rgba(255,255,255,.88)',
              lineHeight: 1.55,
              mb: '20px',
            }}
          >
            한 줄이면 충분해요. 강원캔버스가 동선과 예산까지 그려드려요.
          </Typography>

          {/* Inline input row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#fff',
              borderRadius: '14px',
              p: '6px 6px 6px 18px',
              boxShadow: '0 8px 24px rgba(0,0,0,.12)',
              mb: '14px',
              maxWidth: 620,
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{ color: BRAND, fontSize: 16, mr: '10px', flexShrink: 0 }}
            >
              ✦
            </Box>
            <InputBase
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleStartChat();
              }}
              placeholder="예: 반려견과 강릉 2박 3일, 조용한 카페 위주로"
              inputProps={{ 'aria-label': '여행 계획 입력' }}
              sx={{ flex: 1, fontSize: 13, color: INK_HI }}
            />
            <ButtonBase
              onClick={handleStartChat}
              aria-label="폴리오 그리기 시작"
              sx={{
                background: `linear-gradient(135deg,${BRAND},${ACCENT_STRONG})`,
                color: '#fff',
                borderRadius: '10px',
                px: '16px',
                py: '10px',
                fontSize: 12.5,
                fontWeight: 700,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': { opacity: 0.88 },
                '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
              }}
            >
              그리기 →
            </ButtonBase>
          </Box>

          {/* Quick chips */}
          <Box
            role="group"
            aria-label="빠른 검색 태그"
            sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
          >
            {QUICK_CHIPS.map((chip) => (
              <ButtonBase
                key={chip}
                onClick={() => handleChipClick(chip)}
                aria-label={`${chip} 태그 추가`}
                sx={{
                  // [2026-09-18 대비 상향] 흰 반투명(.18, 배경을 밝힘)은 히어로 배경을 더 밝혀 흰 텍스트
                  // 대비를 깎았다 — 검정 반투명(어둡힘)으로 교체(overlay.scrimOnBrand 패턴, FolioMobile 참조).
                  bgcolor: c.overlay.scrimOnBrand[L],
                  border: '1px solid rgba(255,255,255,.25)',
                  color: '#fff',
                  borderRadius: 99,
                  px: '12px',
                  py: '6px',
                  fontSize: 12,
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'rgba(0,0,0,.5)' },
                  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
                }}
              >
                {chip}
              </ButtonBase>
            ))}
          </Box>
        </Box>

        {/* ── B. 이어서 작성 + 통계 Row — M7 P1 실데이터(비로그인 시 로그인 유도) ───── */}
        {status !== 'authenticated' ? (
          <Box
            component="section"
            aria-label="로그인 유도"
            sx={{
              bgcolor: '#fff',
              border: `1px solid ${BORDER}`,
              borderRadius: '16px',
              p: '18px 20px',
              mb: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: INK_MID }}>
              로그인하면 내 폴리오·좋아요·Fork 현황을 확인할 수 있어요.
            </Typography>
            <ButtonBase
              onClick={() => navigate('/login')}
              aria-label="로그인하러 가기"
              sx={{
                bgcolor: BRAND,
                color: '#fff',
                borderRadius: `${tokens.radius.s}px`,
                px: '16px',
                py: '8px',
                fontSize: 12,
                fontWeight: 700,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              로그인
            </ButtonBase>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' },
              gap: '14px',
              mb: '38px',
            }}
          >
            {/* Left: 이어서 작성 중인 폴리오 — 실데이터(최근 폴리오 1건) 또는 새 폴리오 CTA */}
            <Box
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: '16px',
                p: '18px 20px',
              }}
            >
              {myRecentLoading ? (
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '10px' }} />
              ) : continuingFolio ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg,#1F6FEB,#2BB673)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28,
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {continuingFolio.coverImageUrl ? (
                      <Box
                        component="img"
                        src={continuingFolio.coverImageUrl}
                        alt=""
                        loading="lazy"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      '🌊'
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '5px' }}>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: WARM,
                          flexShrink: 0,
                        }}
                        aria-hidden="true"
                      />
                      <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: WARM }}>
                        최근 폴리오
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: INK_HI,
                        letterSpacing: '-0.2px',
                        mb: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {continuingFolio.title}
                    </Typography>

                    <ButtonBase
                      onClick={() => navigate(`/folio/${continuingFolio.id}/edit`)}
                      aria-label="이어서 편집하기"
                      sx={{
                        bgcolor: PAPER1,
                        color: INK_HI,
                        border: `1px solid ${BORDER}`,
                        borderRadius: '9px',
                        px: '14px',
                        py: '6px',
                        fontSize: 11.5,
                        fontWeight: 600,
                        '&:hover': { bgcolor: PAPER2 },
                        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                      }}
                    >
                      이어서 편집하기 →
                    </ButtonBase>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '14px',
                      border: `2px dashed ${BORDER}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 26,
                      flexShrink: 0,
                      color: INK_LOW,
                    }}
                  >
                    ✦
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, color: INK_MID, mb: '10px' }}>
                      아직 만든 폴리오가 없어요.
                    </Typography>
                    <ButtonBase
                      onClick={() => navigate('/chat')}
                      aria-label="새 폴리오 만들기"
                      sx={{
                        bgcolor: BRAND,
                        color: '#fff',
                        borderRadius: '9px',
                        px: '14px',
                        py: '7px',
                        fontSize: 11.5,
                        fontWeight: 700,
                        '&:hover': { opacity: 0.88 },
                        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                      }}
                    >
                      + 새 폴리오 만들기
                    </ButtonBase>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Right: 통계 — GET /users/me/stats 실데이터 */}
            {/* [수정: 2026-09-20 레이아웃 QA] 세로 나열(세 항목 스택 + 수평 구분선)로 인해 왼쪽
                "이어서 작성" 카드 높이에 맞춰 stretch 되면 카드 하단이 크게 비어보이던 문제 수정 —
                3개 항목을 가로 1줄로 배치(수직 구분선)하고 사이즈를 좁대에 맞게 축소. */}
            <Box
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: '16px',
                p: '18px 20px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* [수정: 디자인 QA 2026-09-20] 통계 카드 이모지(📔❤️⑂) → LineIcons.tsx 공용 라인
                  아이콘으로 교체(stat-folio/stat-like/stat-fork). 값/라벨/데이터 소스(useUserStats)는 불변. */}
              {[
                { n: stats?.folioCount ?? 0, l: '내 폴리오', i: 'stat-folio' as LineIconName },
                { n: stats?.likesReceived ?? 0, l: '받은 좋아요', i: 'stat-like' as LineIconName },
                { n: stats?.forkedCount ?? 0, l: 'Fork됨', i: 'stat-fork' as LineIconName },
              ].map((stat, idx, arr) => (
                <Box
                  key={stat.l}
                  sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: '5px',
                      minWidth: 0,
                    }}
                  >
                    <Box aria-hidden="true" sx={{ color: INK_MID, display: 'flex' }}>
                      <LineIcon name={stat.i} size={16} />
                    </Box>
                    {statsLoading ? (
                      <Skeleton width={30} height={20} />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 17,
                          fontWeight: 800,
                          color: INK_HI,
                          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                          lineHeight: 1.1,
                        }}
                      >
                        {stat.n}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: INK_MID,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {stat.l}
                    </Typography>
                  </Box>
                  {/* 세로 → 가로 전환에 따라 구분선도 수평선(height:1px) → 수직선(width:1px)으로 교체 */}
                  {idx < arr.length - 1 && (
                    <Box
                      sx={{ width: '1px', alignSelf: 'stretch', bgcolor: BORDER_SOFT, mx: '10px' }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── C. 강원 축제 — GET /festivals 실데이터(fe-home-festivals-live) ──── */}
        {showFestivalSection && (
          <Box component="section" aria-label="강원 축제" sx={{ mb: '38px' }}>
            {/* 섹션 헤더 — 링크 대상(/explore) 유지 */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                mb: '14px',
              }}
            >
              <Box>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: INK_HI,
                    letterSpacing: '-0.3px',
                    m: 0,
                  }}
                >
                  강원 축제
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: INK_LOW, mt: '2px' }}>
                  지금 열리는 축제와 지난 축제를 한눈에
                </Typography>
              </Box>
              {/* [수정: 디자인 개편] 이제 전용 축제 페이지(/festivals)가 생겨 더이상 /explore 로 보내지 않는다. */}
              <ButtonBase
                onClick={() => navigate('/festivals')}
                aria-label="축제 전체 보기"
                sx={{
                  color: INK_MID,
                  fontSize: 11.5,
                  fontWeight: 600,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                전체 보기 →
              </ButtonBase>
            </Box>

            {festivalsLoading ? (
              <FestivalSectionSkeleton />
            ) : (
              <>
                {featuredFest && <HomeFestivalHero festival={featuredFest} onNavigate={navigate} />}

                {/* Small festival cards */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: '10px',
                  }}
                >
                  {smallFests.map((fest) => (
                    <HomeFestivalCard key={fest.contentId} festival={fest} onNavigate={navigate} />
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ── D. 큐레이티드 컬렉션 — GET /collections?kind=official 실데이터(M7 P1) ─── */}
        {(collectionsLoading || officialCollections.length > 0) && (
          <Box component="section" sx={{ mb: '36px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                mb: '14px',
              }}
            >
              <Typography
                component="h3"
                sx={{ fontSize: 14, fontWeight: 700, color: INK_HI, letterSpacing: '-0.2px', m: 0 }}
              >
                큐레이티드 컬렉션
              </Typography>
              <ButtonBase
                onClick={() => navigate('/explore')}
                aria-label="컬렉션 전체 보기"
                sx={{
                  color: INK_MID,
                  fontSize: 11.5,
                  fontWeight: 600,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                전체 →
              </ButtonBase>
            </Box>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: `repeat(${colCols}, 1fr)`, gap: '10px' }}
            >
              {collectionsLoading
                ? [...Array(colCols * 2)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={90}
                      sx={{ borderRadius: `${tokens.radius.m}px` }}
                    />
                  ))
                : officialCollections
                    .slice(0, viewport.isMobile ? 4 : colCols * 2)
                    .map((col: Collection) => (
                      <ButtonBase
                        key={col.id}
                        onClick={() => navigate(`/collections/${col.id}`)}
                        aria-label={`컬렉션 ${col.title}`}
                        sx={{
                          bgcolor: PAPER2,
                          borderRadius: `${tokens.radius.m}px`,
                          p: '14px 14px 13px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '8px',
                          minHeight: 90,
                          textAlign: 'left',
                          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                        }}
                      >
                        <Box component="span" aria-hidden="true" sx={{ fontSize: 24 }}>
                          🏛
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: INK_HI,
                              letterSpacing: '-0.2px',
                            }}
                          >
                            {col.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: INK_MID,
                              mt: '2px',
                              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                            }}
                          >
                            {col.itemCount} folios
                          </Typography>
                        </Box>
                      </ButtonBase>
                    ))}
            </Box>
          </Box>
        )}

        {/* ── E. 이번 주 추천 폴리오 — 실데이터 ──────────────────────────── */}
        <Box component="section">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: '14px',
            }}
          >
            <Typography
              component="h3"
              sx={{ fontSize: 14, fontWeight: 700, color: INK_HI, letterSpacing: '-0.2px', m: 0 }}
            >
              이번 주 추천 폴리오
            </Typography>
            <ButtonBase
              onClick={() => navigate('/explore')}
              aria-label="발견 탭에서 더보기"
              sx={{
                color: INK_MID,
                fontSize: 11.5,
                fontWeight: 600,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              발견 탭에서 더보기 →
            </ButtonBase>
          </Box>

          {Boolean(feedError) && !feedLoading && (
            <Box
              role="alert"
              sx={{ textAlign: 'center', p: '32px 20px', color: INK_MID, fontSize: 13 }}
            >
              추천 피드를 불러오지 못했습니다.
            </Box>
          )}

          {feedLoading && (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px' }}>
              {[...Array(cols * 2)].map((_, i) => (
                <FolioCardSkeleton key={i} />
              ))}
            </Box>
          )}

          {!feedLoading && allFolios.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px' }}>
              {allFolios.slice(0, cols * 2).map((f) => (
                <HomeFolioCard key={f.id} folio={f} onClick={() => navigate(`/folio/${f.id}`)} />
              ))}
            </Box>
          )}

          {/* [수정: 디자인 개편] 추천 폴리오가 0건일 때 화면이 빈 텍스트로만 채워져 업용해 보였다 —
              첫 칸에 "새 폴리오 만들기" 카드를 고정해 화면이 비지 않도록 한다. */}
          {!feedLoading && allFolios.length === 0 && !feedError && (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '14px' }}>
              <ButtonBase
                onClick={() => navigate('/chat')}
                aria-label="새 폴리오 만들기"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  aspectRatio: '1 / 1.1',
                  border: `2px dashed ${BORDER}`,
                  borderRadius: '14px',
                  color: INK_LOW,
                  '&:hover': { borderColor: BRAND, color: BRAND },
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                <Box aria-hidden="true" sx={{ fontSize: 26 }}>
                  ✦
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>+ 새 폴리오 만들기</Typography>
              </ButtonBase>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

// ─── HomeFolioCard ─────────────────────────────────────────────────────────────

/**
 * [목적] 홈 추천 피드 폴리오 카드 — 실데이터(Folio) 기반 렌더링
 * [이유] design-align-v2: 기존 카드 컴포넌트 유지 (레이아웃 변경 없음).
 *         fe-folio-social-wire: FolioLikeChip 으로 좋아요 실배선.
 * [사이드 이팩트] 없음 — FolioLikeChip 이 자체 캐시로 상태 관리.
 * [수정 시 고려사항] coverImageUrl 없으면 PAPER2 배경 + 이모지 폴백.
 */
function HomeFolioCard({ folio, onClick }: { folio: Folio; onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`폴리오 ${folio.title} 열기`}
      sx={{
        display: 'block',
        width: '100%',
        bgcolor: '#fff',
        borderRadius: `${tokens.radius.m}px`,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        textAlign: 'left',
        transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
        boxShadow: tokens.shadow.card,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: tokens.shadow.modal },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      {/* 커버 */}
      <Box
        sx={{
          height: 140,
          bgcolor: PAPER2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 48,
          position: 'relative',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {folio.coverImageUrl ? (
          <Box
            component="img"
            src={folio.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}
        {folio.duration.days && (
          <Box
            sx={{
              position: 'absolute',
              top: 9,
              left: 9,
              bgcolor: 'rgba(255,255,255,.95)',
              borderRadius: `${tokens.radius.s}px`,
              px: '8px',
              py: '3px',
              fontSize: 10,
              color: INK_HI,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {folio.duration.days}일
          </Box>
        )}
      </Box>

      {/* 본문 */}
      <Box sx={{ p: '12px 14px 13px' }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: INK_HI,
            lineHeight: 1.4,
            mb: '8px',
            letterSpacing: '-0.2px',
          }}
        >
          {folio.title}
        </Typography>

        {folio.vibe.length > 0 && (
          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mb: '10px' }}>
            {folio.vibe.slice(0, 3).map((v) => (
              <Box
                key={v}
                component="span"
                sx={{
                  fontSize: 10,
                  bgcolor: PAPER2,
                  color: INK_MID,
                  borderRadius: `${tokens.radius.s}px`,
                  px: '7px',
                  py: '2px',
                  fontWeight: 500,
                }}
              >
                #{v}
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: '8px',
            borderTop: `1px solid ${BORDER_SOFT}`,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 10.5,
              color: INK_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {folioAuthorLabel(folio.author)}
          </Box>
          <FolioLikeChip folio={folio} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

// ─── HomeFestivalHero ──────────────────────────────────────────────────────────

/**
 * [목적] 강원 축제 섹션 히어로 배너 — 목록 첫 항목(서버 정렬 1순위) 렌더
 * [이유] fe-home-festivals-live: firstImage 를 배경 이미지로 사용하되, 이미지가 없거나 로드 실패(onError)
 *        시 기존(현행) 오렌지 그라디언트로 폴백해 시각적 회귀를 막는다. 상태 배지 색만 status 에 따라 변경.
 * [사이드 이팩트] navigate('/explore') 호출 — 기존 "축제 보기" 링크 대상과 동일(변경 없음).
 * [수정 시 고려사항] overview/addr1 이 없으면 해당 줄 자체를 렌더하지 않음(instructions: 없으면 생략).
 */
function HomeFestivalHero({
  festival,
  onNavigate,
}: {
  festival: Festival;
  onNavigate: (path: string) => void;
}): JSX.Element {
  const [imgFailed, setImgFailed] = useState(false);
  const meta = STATUS_META[festival.status];
  const period = formatFestivalPeriod(festival.startDate, festival.endDate);
  const location = formatFestivalLocation(festival.addr1);
  const overview = truncateOverview(festival.overview);
  const showImage = Boolean(festival.firstImage) && !imgFailed;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '18px',
        minHeight: { xs: 140, md: 168 },
        boxShadow: tokens.shadow.card,
        mb: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: { xs: '22px 20px', md: '28px 32px' },
        gap: '16px',
      }}
    >
      {/* 배경 — 현행 오렌지 그라디언트(WARM 토큰) 기본, 이미지 로드 성공 시 그 위에 표시 */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${alpha(WARM, 0.85)}, ${WARM})`,
        }}
      />
      {showImage && (
        <>
          <Box
            component="img"
            src={festival.firstImage ?? undefined}
            alt=""
            loading="lazy"
            onError={() => setImgFailed(true)}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {/* 텍스트 대비 확보용 색 오버레이(alpha 토큰 유틸 — hex/rgba 직접 입력 없음) */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(135deg, ${alpha(WARM, 0.55)}, ${alpha(WARM, 0.78)})`,
            }}
          />
        </>
      )}

      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {/* 상태 배지 — 진행 중=초록/예정=브랜드/종료=회색 + 텍스트 라벨(색만으로 전달하지 않음) */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            bgcolor: PAPER0,
            borderRadius: 99,
            px: '10px',
            py: '4px',
            mb: '10px',
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: meta.color,
              animation:
                festival.status === 'ongoing' ? 'gwPulse 1.6s ease-in-out infinite' : 'none',
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: meta.textColor }}>
            {meta.label}
          </Typography>
        </Box>

        <Typography
          component="h2"
          sx={{
            fontSize: { xs: 18, md: 22 },
            fontWeight: 800,
            color: PAPER0,
            letterSpacing: '-0.4px',
            m: 0,
            mb: '6px',
            lineHeight: 1.2,
          }}
        >
          {festival.title}
        </Typography>

        {(period || location) && (
          <Typography
            sx={{
              fontSize: 11.5,
              color: alpha(PAPER0, 0.85),
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              mb: '5px',
            }}
          >
            {[period, location].filter(Boolean).join(' · ')}
          </Typography>
        )}

        {overview && (
          <Typography sx={{ fontSize: 12.5, color: alpha(PAPER0, 0.82), lineHeight: 1.5 }}>
            {overview}
          </Typography>
        )}
      </Box>

      {/* [수정: 디자인 개편] /explore 대신 실제 축제 상세 페이지(/festivals/:id)로 이동 */}
      <ButtonBase
        onClick={() => onNavigate(`/festivals/${festival.contentId}`)}
        aria-label={`${festival.title} 축제 보기`}
        sx={{
          bgcolor: PAPER0,
          color: meta.color,
          borderRadius: 99,
          px: '16px',
          py: '9px',
          fontSize: 12,
          fontWeight: 700,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          '&:hover': { opacity: 0.88 },
          '&:focus-visible': { outline: `2px solid ${PAPER0}`, outlineOffset: 2 },
        }}
      >
        축제 보기 →
      </ButtonBase>
    </Box>
  );
}

// ─── HomeFestivalCard ──────────────────────────────────────────────────────────

/**
 * [목적] 강원 축제 섹션 작은 카드 3개 — 목록 2~4번째 항목 렌더
 * [이유] fe-home-festivals-live: 썸네일은 firstImage(작은 라운드 이미지), 실패/無이미지 시 기존 이모지 폴백.
 *        상태 배지는 히어로와 동일 STATUS_META 사용.
 * [사이드 이팩트] navigate('/explore') 호출 — 기존 카드 클릭 동작과 동일(변경 없음).
 * [수정 시 고려사항] ended 카드는 기존처럼 grayscale 필터 + "종료" 텍스트 배지 병행(색만으로 전달 금지).
 */
function HomeFestivalCard({
  festival,
  onNavigate,
}: {
  festival: Festival;
  onNavigate: (path: string) => void;
}): JSX.Element {
  const [imgFailed, setImgFailed] = useState(false);
  const meta = STATUS_META[festival.status];
  const ended = festival.status === 'ended';
  const period = formatFestivalPeriod(festival.startDate, festival.endDate);
  const showImage = Boolean(festival.firstImage) && !imgFailed;

  return (
    <ButtonBase
      onClick={() => onNavigate(`/festivals/${festival.contentId}`)}
      aria-label={`${festival.title} 축제${ended ? ' (종료)' : ''}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        bgcolor: PAPER0,
        border: `1px solid ${BORDER}`,
        borderRadius: '14px',
        p: '14px 16px',
        textAlign: 'left',
        filter: ended ? 'grayscale(.35)' : 'none',
        '&:hover': { bgcolor: PAPER1 },
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      {/* 썸네일 — firstImage 있으면 이미지, 없거나 실패하면 이모지 폴백 */}
      <Box
        aria-hidden="true"
        sx={{
          width: 50,
          height: 50,
          borderRadius: '13px',
          background: ended ? PAPER2 : alpha(WARM, 0.18),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          flexShrink: 0,
          overflow: 'hidden',
          filter: ended ? 'grayscale(1)' : 'none',
        }}
      >
        {showImage ? (
          <Box
            component="img"
            src={festival.firstImage ?? undefined}
            alt=""
            loading="lazy"
            onError={() => setImgFailed(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🎪'
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'inline-block',
            bgcolor: ended ? PAPER2 : alpha(meta.color, 0.13),
            color: meta.textColor,
            borderRadius: 99,
            px: '7px',
            py: '2px',
            fontSize: 9.5,
            fontWeight: 600,
            mb: '5px',
          }}
        >
          {meta.label}
        </Box>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: INK_HI,
            letterSpacing: '-0.2px',
            mb: '3px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {festival.title}
        </Typography>

        {period && (
          <Typography
            sx={{
              fontSize: 10.5,
              color: INK_LOW,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {period}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

// ─── FestivalSectionSkeleton ───────────────────────────────────────────────────

/**
 * [목적] 강원 축제 섹션 로딩 스켈레톤 — 히어로 1개 + 카드 3개 자리 유지(CLS 방지)
 * [이유] fe-home-festivals-live: 기존 FolioCardSkeleton 패턴과 동일하게 실제 치수에 맞춘 자리표시자 제공.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 히어로/카드 실제 minHeight·height 와 동기화 유지(레이아웃 이동 방지 목적).
 */
function FestivalSectionSkeleton(): JSX.Element {
  return (
    <>
      <Skeleton
        variant="rectangular"
        sx={{ minHeight: { xs: 140, md: 168 }, borderRadius: '18px', mb: '10px' }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: '10px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: '14px' }} />
        ))}
      </Box>
    </>
  );
}
